import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import { useCaseDetails } from '../data/useCaseDetails';

const UseCaseDetail: React.FC = () => {
  const { useCaseId } = useParams<{ useCaseId: string }>();
  const useCase = useCaseId ? useCaseDetails[useCaseId] : null;

  if (!useCase) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        <div className="max-w-4xl mx-auto px-4 py-12 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Use Case Not Found</h1>
            <p className="text-gray-600 mb-6">The use case you're looking for doesn't exist or the link may be incorrect.</p>
            <Link to="/use-cases" className="text-primary-600 hover:text-primary-700 font-medium">
              ← Back to Use Cases
            </Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  const Icon = useCase.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <PublicHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <article className="bg-white rounded-lg shadow-md p-4 sm:p-6 lg:p-8">
          <div className="flex items-center mb-4 sm:mb-6">
            <Icon className="h-10 w-10 sm:h-12 sm:w-12 text-primary-600 mr-3 sm:mr-4 flex-shrink-0" />
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">{useCase.title}</h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mt-2">{useCase.description}</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none mb-8">
            <div dangerouslySetInnerHTML={{ __html: useCase.content }} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Key Benefits</h3>
              <ul className="space-y-2">
                {useCase.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Common Use Cases</h3>
              <ul className="space-y-2">
                {useCase.useCases.map((uc, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{uc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Ideal For</h3>
            <div className="flex flex-wrap gap-2">
              {useCase.industries.map((industry, index) => (
                <span
                  key={index}
                  className="bg-white text-primary-600 px-4 py-2 rounded-full text-sm font-medium border border-primary-200"
                >
                  {industry}
                </span>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8">
            <div className="flex justify-between items-center">
              <Link
                to="/use-cases"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                â† View All Use Cases
              </Link>
              <Link
                to="/contact"
                className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold"
              >
                Contact Us for Evaluation Access
              </Link>
            </div>
          </div>
        </article>
      </div>
      <PublicFooter />
    </div>
  );
};

export default UseCaseDetail;

